import { useId, useMemo, type ReactNode } from 'react';
import {
  DOCUMENT_TEMPLATES,
  buildLocationTree,
  documentTemplate,
  formatGermanDate,
  locationLabel,
  requiresSeries,
  seriesBelowLocation,
  type DocumentTypeKey,
  type LocationNode,
  type LocationTreeNode,
  type MeasurementSeries,
} from '@oscom/shared';

export interface DocumentFieldValues {
  documentTypeKey: DocumentTypeKey;
  title: string;
  subtitle: string;
  siteLocationId: string;
  seriesIds: string[];
}

function flatten(
  tree: readonly LocationTreeNode[],
  depth = 0,
): Array<{ node: LocationNode; depth: number }> {
  return tree.flatMap((entry) => [
    { node: entry.node, depth },
    ...flatten(entry.children, depth + 1),
  ]);
}

/** The first root of the location tree - usually the site. */
export function defaultSiteLocationId(locations: readonly LocationNode[]): string {
  return buildLocationTree(locations)[0]?.node.id ?? '';
}

export function documentFieldsComplete(values: DocumentFieldValues): boolean {
  const template = documentTemplate(values.documentTypeKey);
  if (!template || values.title.trim() === '' || values.siteLocationId === '') return false;
  return !requiresSeries(template) || values.seriesIds.length > 0;
}

/**
 * Template, title, subtitle, location and the series below that location.
 * Shared by the create dialog and the document settings in the editor (there
 * the template is fixed: the chapters were created from it).
 */
export function DocumentFields({
  values,
  onChange,
  locations,
  series,
  seriesListed = true,
  templateFixed,
}: {
  values: DocumentFieldValues;
  onChange: (values: DocumentFieldValues) => void;
  locations: readonly LocationNode[];
  series: readonly MeasurementSeries[];
  /**
   * False when the series list could not be loaded (measurements switched
   * off): a chosen series missing from it is then unknown, not deleted.
   */
  seriesListed?: boolean;
  templateFixed: boolean;
}): ReactNode {
  const ids = { template: useId(), title: useId(), subtitle: useId(), location: useId() };
  const options = useMemo(() => flatten(buildLocationTree(locations)), [locations]);
  const byId = useMemo(() => new Map(locations.map((node) => [node.id, node])), [locations]);
  const seriesById = useMemo(() => new Map(series.map((entry) => [entry.id, entry])), [series]);
  const available = values.siteLocationId
    ? seriesBelowLocation(series, byId, values.siteLocationId)
    : [];
  // Chosen series that cannot be offered (deleted, elsewhere or unknown).
  // They stay visible and checked so the user can uncheck them; dropping
  // them silently would change the document behind the user's back.
  const availableIds = new Set(available.map((entry) => entry.id));
  const stale = values.seriesIds.filter((id) => !availableIds.has(id));
  const template = documentTemplate(values.documentTypeKey);
  const needsSeries = template ? requiresSeries(template) : false;
  const set = (patch: Partial<DocumentFieldValues>): void => onChange({ ...values, ...patch });

  function chooseLocation(siteLocationId: string): void {
    const inScope = new Set(
      seriesBelowLocation(series, byId, siteLocationId).map((entry) => entry.id),
    );
    // Only known series outside the new location are dropped; unknown ones
    // (e.g. measurements switched off) stay for the user to decide.
    set({
      siteLocationId,
      seriesIds: values.seriesIds.filter((id) => inScope.has(id) || !seriesById.has(id)),
    });
  }

  function staleLabel(id: string): string {
    const known = seriesById.get(id);
    if (known) {
      return `${known.label} (${formatGermanDate(known.measuredOn)}) – nicht unter dem gewählten Ort`;
    }
    return `Messreihe ${id} – ${seriesListed ? 'nicht mehr vorhanden' : 'nicht mehr verfügbar'}`;
  }

  function toggleSeries(id: string, checked: boolean): void {
    set({
      seriesIds: checked
        ? [...values.seriesIds, id]
        : values.seriesIds.filter((entry) => entry !== id),
    });
  }

  return (
    <div className="stack">
      <div className="form-field">
        <label htmlFor={ids.template}>Vorlage</label>
        <select
          id={ids.template}
          value={values.documentTypeKey}
          disabled={templateFixed}
          onChange={(event) => set({ documentTypeKey: event.target.value as DocumentTypeKey })}
        >
          {DOCUMENT_TEMPLATES.map((entry) => (
            <option key={entry.key} value={entry.key}>
              {entry.name}
            </option>
          ))}
        </select>
      </div>
      <div className="form-field">
        <label htmlFor={ids.title}>Titel</label>
        <input
          id={ids.title}
          type="text"
          maxLength={200}
          aria-required="true"
          value={values.title}
          onChange={(event) => set({ title: event.target.value })}
        />
      </div>
      <div className="form-field">
        <label htmlFor={ids.subtitle}>Untertitel</label>
        <input
          id={ids.subtitle}
          type="text"
          maxLength={200}
          value={values.subtitle}
          onChange={(event) => set({ subtitle: event.target.value })}
        />
      </div>
      <div className="form-field">
        <label htmlFor={ids.location}>Ort</label>
        <select
          id={ids.location}
          aria-required="true"
          value={values.siteLocationId}
          onChange={(event) => chooseLocation(event.target.value)}
        >
          <option value="">(bitte wählen)</option>
          {options.map(({ node, depth }) => (
            <option key={node.id} value={node.id}>
              {'— '.repeat(depth)}
              {locationLabel(node)}
            </option>
          ))}
        </select>
      </div>
      <fieldset className="form-field series-choice">
        <legend>Messreihen</legend>
        {available.length === 0 ? (
          <p className="muted">Unter diesem Ort liegt keine Messreihe.</p>
        ) : (
          available.map((entry) => (
            <label key={entry.id} className="series-choice__entry">
              <input
                type="checkbox"
                checked={values.seriesIds.includes(entry.id)}
                onChange={(event) => toggleSeries(entry.id, event.target.checked)}
              />{' '}
              {entry.label} ({formatGermanDate(entry.measuredOn)})
            </label>
          ))
        )}
        {stale.map((id) => (
          <label key={id} className="series-choice__entry series-choice__entry--stale">
            <input
              type="checkbox"
              checked
              onChange={(event) => toggleSeries(id, event.target.checked)}
            />{' '}
            {staleLabel(id)}
          </label>
        ))}
        {needsSeries && values.seriesIds.length === 0 ? (
          <p className="muted">Für diese Vorlage ist mindestens eine Messreihe nötig.</p>
        ) : null}
      </fieldset>
    </div>
  );
}
